// Turns "who is at the monitor" into session actions: sign in, switch user, or lock.
//
// ── The rules ────────────────────────────────────────────────────────────────
//
//   Session      Camera sees            Action
//   ─────────    ───────────────────    ────────────────────────────────────
//   locked       enrolled user          sign them in
//   locked       stranger / nobody      nothing (stay locked)
//   as A         A (with anyone)        nothing — owner present
//   as A         B alone (enrolled)     switch to B
//   as A         stranger alone         warn, then lock after the grace period
//   as A         nobody                 nothing — the idle timer owns this
//
// OWNER PRESENT WINS is the rule that makes this usable. Every "someone else is here" branch
// requires the logged-in user to be *absent* from frame. A colleague reading over your shoulder,
// a patient walking behind you, someone leaning in to point at your screen — none of it can take
// your session away while you are sitting there. Without this rule the feature is unusable in a
// clinic, where people are constantly in each other's frame.
//
// ── Why the debounce matters ─────────────────────────────────────────────────
// Every action here is disruptive and effectively irreversible from the user's seat, so nothing
// fires on a single frame. A face has to hold the same verdict for CONFIRM_FRAMES consecutive
// observations (~1.5s) before it counts. That kills the two failure modes that make naive face
// switching intolerable: one bad frame as someone walks past, and flapping between two verdicts
// while a face is half-turned.

import { loginWithPin } from "@/lib/api/auth";
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";
import { lockSession } from "@/lib/auth/session-lock";
import {
  getEnrolledPin,
  getEnrollment,
  getEnrollments,
  subscribeFaceEnrollments,
} from "@/lib/face/face-enrollment-store";
import {
  isFaceWatchSuspended,
  setFaceWatchPace,
  startFaceWatch,
  stopFaceWatch,
  subscribeFaceObservations,
} from "@/lib/face/face-watcher";
import { getUserPreferences, subscribeUserPreferences } from "@/lib/stores/user-preference-store";

// Consecutive observations that must agree before acting. At the watcher's 500ms cadence this
// is ~1.5s of "yes, really, this person".
const CONFIRM_FRAMES = 3;
// How long the owner must have gone UNRECOGNIZED before we will warn a stranger and lock is
// configurable (Settings ▸ Face unlock, faceOwnerAbsentSeconds, default 60s) — the right value
// depends on the room, and only the people in it know it.
//
// Why the delay exists at all: "stranger" means only "a face we could not match" — and the face
// that most often fails to match is the OWNER'S OWN: looking down at notes, turning to talk to
// someone, a hand across the chin, harsh backlight. A few seconds of that and an early build
// told Marcin that this was Marcin's session.
//
// So a confirmed stranger is NOT evidence the owner left; it is evidence of one unmatched face.
// Only sustained absence is evidence, and a minute of it cannot be produced by glancing away.
// The cost of being slow here is a stranger reading a screen for a bit longer; the cost of being
// fast is locking clinicians out of their own session mid-task, which is how a security feature
// gets switched off for good. Hence the 10s floor on the setting.
// After any action, ignore the camera for this long. Sign-in and switching both re-render the
// whole app and re-run the auth bridge; acting again mid-transition would stack a second switch
// on top of the first. Also stops a stranger-lock from instantly re-triggering.
const ACTION_COOLDOWN_MS = 4000;
// A verdict older than this is stale — the camera stalled or the app was asleep. Better to
// re-confirm from scratch than to act on what someone's face was doing a minute ago.
const VERDICT_TTL_MS = 3000;

const INTRUDER_CHANGE_EVENT = "healo-face-intruder-change";

let installed = false;
let watching = false;
// The camera id the running watch was opened with, so a change in Settings can be noticed.
let watchingDeviceId = "";
// The verdict we're accumulating confirmations for, e.g. "switch to user X" / "stranger".
let pendingVerdict = null;
let pendingCount = 0;
let pendingAt = 0;
let cooldownUntil = 0;
// Epoch ms the owner was last RECOGNIZED at the monitor — the clock behind
// faceOwnerAbsentSeconds. Seeded (not left at 0) whenever a session starts or the camera
// starts watching, so absence is only ever measured over time we could actually see: having
// been blind for an hour is not evidence that anyone was away.
let ownerLastSeenAt = 0;
// Non-null while a stranger is being warned: {deadlineMs}. Drives the countdown overlay.
let intruder = null;
let intruderTimerId = null;
// Guards against a second action being launched while an await is in flight (loginWithPin is a
// network round-trip — several observations land during it).
let acting = false;

function emitIntruderChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(INTRUDER_CHANGE_EVENT));
}

/** {deadlineMs} while a stranger countdown is running, else null. */
export function getFaceIntruder() {
  return intruder;
}

export function subscribeFaceIntruder(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") return () => {};
  const handler = () => listener(intruder);
  window.addEventListener(INTRUDER_CHANGE_EVENT, handler);
  return () => window.removeEventListener(INTRUDER_CHANGE_EVENT, handler);
}

function clearIntruder() {
  if (intruderTimerId !== null) {
    clearTimeout(intruderTimerId);
    intruderTimerId = null;
  }
  if (intruder) {
    intruder = null;
    emitIntruderChange();
  }
}

function resetPending() {
  pendingVerdict = null;
  pendingCount = 0;
  pendingAt = 0;
}

/**
 * Fold an observation into a verdict.
 *
 * Note the asymmetry on spoofing: `isRealPerson` gates only the branches that GRANT access
 * (sign-in, switch). A photo held to the camera still counts as a stranger and still locks the
 * session — otherwise holding up a picture would be a way to suppress the lock, turning the
 * anti-spoof model into an attack tool.
 */
function classify(allFaces, activeUserId) {
  // Everything below reasons about who is USING this machine, so the background is dropped up
  // front: a face across the room is neither a user nor an intruder. Without this, anyone
  // walking past behind an empty chair is an unrecognized face and would lock the session.
  const faces = allFaces.filter((face) => face.isAtMonitor);
  const isLocked = !activeUserId;

  if (isLocked) {
    // Temporarily ignore liveness/anti-spoof for lock-screen sign-in when the face matches.
    // Restore `face.isRealPerson` once presentation-attack gating is reliable again.
    // const known = faces.find((face) => face.userId && face.isRealPerson);
    const known = faces.find((face) => face.userId);
    return known ? { kind: "login", userId: known.userId } : { kind: "idle" };
  }

  // Owner present wins — checked before anything else, and deliberately without a liveness
  // requirement: a real user whose liveness score dips for a frame is still present, and
  // demanding proof-of-life to *keep* your own session would make it flap. (Never true for an
  // unenrolled owner — nothing can match them — which the branches below account for.)
  if (faces.some((face) => face.userId === activeUserId)) return { kind: "owner" };

  if (!faces.length) return { kind: "idle" };

  const other = faces.find(
    (face) => face.userId && face.userId !== activeUserId && face.isRealPerson,
  );
  // Faces we cannot put a name to. Any one of them might BE the logged-in user — never enrolled,
  // or just a bad frame of someone who is — so while one is at the monitor we must not act on
  // anyone else.
  const hasUnidentified = faces.some((face) => !face.userId);

  if (other) {
    // A colleague is positively identified, so switch to them. This deliberately does NOT
    // require the LOGGED-IN user to be enrolled: matching Ana proves Ana is sitting here,
    // whoever happens to be signed in. (An earlier build demanded it, which meant signing in as
    // an unenrolled test account silently disabled switching entirely.)
    //
    // It does require her to be the only one here. An unidentified face beside her could be the
    // owner — enrolled but momentarily unmatched, or never enrolled at all — and taking someone's
    // session out from under them while they sit at it is exactly what owner-present-wins exists
    // to prevent. This is that same rule, extended to owners we cannot recognize.
    return hasUnidentified ? { kind: "unjudgeable" } : { kind: "switch", userId: other.userId };
  }

  // Nobody here is identified. That only means "an intruder" if we could have recognized the
  // owner in the first place: an unenrolled owner's own face lands here on every single frame,
  // and calling it a stranger would lock them out of their own desk, repeatedly.
  if (!getEnrollment(activeUserId)) return { kind: "unjudgeable" };
  return { kind: "stranger" };
}

// Verdicts are compared by identity, not just kind: "switch to Ana" followed by "switch to Ben"
// must restart the count rather than confirm on the third frame having never seen either face
// three times.
function sameVerdict(a, b) {
  return Boolean(a) && Boolean(b) && a.kind === b.kind && a.userId === b.userId;
}

async function signInWithFace(userId) {
  const enrollment = getEnrollment(userId);
  if (!enrollment) return;
  const pin = await getEnrolledPin(userId);
  if (!pin) {
    // Recognized, but there is no usable credential (enrolled without a PIN, or the vault
    // couldn't unseal it). Nothing to do silently — the user signs in the normal way.
    console.warn("[Face] recognized user has no usable stored PIN; skipping auto sign-in");
    return;
  }
  try {
    // The ordinary PIN-switch path — same call the PIN dialog makes. Everything downstream
    // (fresh JWT, roster upsert, workspace freeze/thaw) therefore behaves identically to a
    // human typing their PIN, which is exactly the property worth having here.
    await loginWithPin({ loginId: enrollment.loginId, pin });
  } catch (error) {
    // A stale PIN (changed on another device) 401s here. Staying quiet is deliberate: the
    // lock screen is already the correct next step, and a toast would leak that this face is
    // enrolled to anyone standing in front of the camera.
    console.error("[Face] auto sign-in failed:", error);
  }
}

function beginIntruderCountdown() {
  if (intruder) return;
  const graceMs = getUserPreferences().faceStrangerGraceSeconds * 1000;
  intruder = { deadlineMs: Date.now() + graceMs };
  emitIntruderChange();
  intruderTimerId = setTimeout(() => {
    intruderTimerId = null;
    clearIntruder();
    // Re-check rather than trusting the countdown: the owner may have sat back down, or logged
    // out themselves, in the seconds since the warning appeared.
    if (getAuthToken()) {
      lockSession();
      cooldownUntil = Date.now() + ACTION_COOLDOWN_MS;
    }
  }, graceMs);
}

async function handleObservation(observation) {
  const preferences = getUserPreferences();
  const activeUserId = String(getAuthUser()?.id || "");
  const verdict = classify(observation.faces, activeUserId);

  // Before the cooldown guard: how hard to look is a question about the SCENE, and it must keep
  // tracking the scene even while we're forbidden from acting on it — otherwise the loop would
  // still be crawling at the moment the cooldown lifts.
  applyPace(verdict);

  if (acting || Date.now() < cooldownUntil) return;

  // A single matched frame is enough to prove the owner is here — no confirmation streak. The
  // asymmetry is deliberate: one frame of recognition is hard evidence of presence, while one
  // frame of non-recognition is barely evidence of anything.
  if (verdict.kind === "owner") ownerLastSeenAt = Date.now();

  // The owner reappearing during a countdown calls it off immediately — no confirmation frames.
  // Being slow to *stop* warning the rightful user is far worse than being slow to start.
  if (verdict.kind === "owner" || verdict.kind === "login") clearIntruder();

  if (!sameVerdict(verdict, pendingVerdict) || Date.now() - pendingAt > VERDICT_TTL_MS) {
    pendingVerdict = verdict;
    pendingCount = 1;
    pendingAt = Date.now();
  } else {
    pendingCount += 1;
    pendingAt = Date.now();
  }

  // These verdicts aren't actions, but they still had to be recorded above so an owner/idle
  // streak properly displaces a half-confirmed switch.
  if (verdict.kind === "owner" || verdict.kind === "idle" || verdict.kind === "unjudgeable") {
    if (verdict.kind !== "idle") clearIntruder();
    return;
  }
  if (pendingCount < CONFIRM_FRAMES) return;

  if (verdict.kind === "stranger") {
    if (!preferences.faceLockOnStrangerEnabled) return;
    // The confirmation streak above only establishes "an unmatched face is sitting here". This
    // is the check that establishes the owner actually left, and it is the one doing the real
    // work — see the note on faceOwnerAbsentSeconds above. Read per-observation so a change in
    // Settings takes effect immediately rather than at the next restart.
    if (Date.now() - ownerLastSeenAt < preferences.faceOwnerAbsentSeconds * 1000) return;
    beginIntruderCountdown();
    return;
  }

  // login + switch both mint a session, so both answer to the auto-switch preference.
  if (!preferences.faceAutoSwitchEnabled) return;

  acting = true;
  resetPending();
  cooldownUntil = Date.now() + ACTION_COOLDOWN_MS;
  try {
    await signInWithFace(verdict.userId);
  } finally {
    acting = false;
  }
}

// How hard to look, decided by what we're looking AT rather than by whether a session exists.
//
// Calm is for exactly one scene: the owner sitting at their own signed-in session. That is the
// case that runs all shift behind real work, and the case where nothing is going to happen no
// matter how often we look. EVERY other scene has a person waiting on a decision — someone at a
// lock screen, a colleague who just sat down at your session, a face we can't place — and there
// the loop is not competing with anyone's work.
//
// The first cut of this keyed off "am I logged in?", which meant a colleague sitting down at
// your session was judged at 2fps: three confirming frames, so ~1.5s before the switch even
// started. Someone is sat there waiting either way; the lock screen is not special.
function applyPace(verdict) {
  setFaceWatchPace(verdict?.kind === "owner" ? "calm" : "fast");
}

// Single place that decides whether the camera should be open: the feature switch, plus at
// least one enrolled face. An enabled-but-empty install has nothing it could recognize, so
// holding the camera open (and the indicator light on) would buy the user nothing and cost
// them their trust.
function syncWatchState() {
  const { faceUnlockEnabled, faceCameraDeviceId } = getUserPreferences();
  const shouldWatch = faceUnlockEnabled && getEnrollments().length > 0 && !isFaceWatchSuspended();

  // Switching camera while already watching changes neither shouldWatch nor watching, so
  // without this the picker in Settings would silently do nothing until the next restart. The
  // device is only re-read when a stream is opened, so it has to be closed and reopened.
  if (shouldWatch && watching && faceCameraDeviceId !== watchingDeviceId) {
    watching = false;
    stopFaceWatch();
  }

  if (shouldWatch && !watching) {
    watching = true;
    watchingDeviceId = faceCameraDeviceId;
    // The camera opens knowing nothing. Start the absence clock now rather than at 0, so
    // enabling face unlock can't instantly conclude the owner has been gone since the epoch.
    ownerLastSeenAt = Date.now();
    void startFaceWatch();
  } else if (!shouldWatch && watching) {
    watching = false;
    stopFaceWatch();
    clearIntruder();
    resetPending();
  }
}

/**
 * Install the face-unlock policy. Idempotent. Safe to call unconditionally at startup — it
 * opens nothing until the preference is on, and the Human models aren't imported until then.
 */
export function registerFaceAuthPolicy() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  subscribeFaceObservations((observation) => void handleObservation(observation));
  subscribeUserPreferences(syncWatchState);
  // Enrolling the first face should start the camera without a settings toggle round-trip.
  subscribeFaceEnrollments(syncWatchState);
  // Any session change (login, switch, lock, 401) invalidates a half-accumulated verdict and
  // any countdown — they were reasoned about against a session that no longer exists.
  subscribeAuthStateChange(() => {
    resetPending();
    clearIntruder();
    // The scene just changed out from under us and the old verdict described the previous
    // session. No verdict = look hard until a frame tells us otherwise; the next observation
    // settles it back to calm if the new owner is simply sitting there.
    applyPace(null);

    if (!getAuthToken()) {
      // Just locked or logged out. The cooldown exists to stop a second action landing on top
      // of an in-flight session switch — but there is no session left to protect, and someone
      // is very likely stood right there waiting to be let back in. Making them watch a frozen
      // lock screen for four seconds is the single most infuriating way this feature can fail,
      // so re-recognition starts immediately.
      cooldownUntil = 0;
      return;
    }

    // A new session means a new owner, so the old owner's absence clock is meaningless — and
    // left unreset it would be long expired, letting the incoming user be warned as a stranger
    // seconds after signing in. Whoever just signed in gets a full minute like anyone else.
    ownerLastSeenAt = Date.now();
    // Whoever just arrived must not be immediately re-judged by frames captured pre-switch.
    cooldownUntil = Date.now() + ACTION_COOLDOWN_MS;
  });

  // Nothing seen yet, so start fast; the first observation settles the pace.
  applyPace(null);
  syncWatchState();
}

/**
 * Cancel the countdown and keep the session — the "It's me" escape hatch on the overlay.
 *
 * Resetting the absence clock is the whole point. Someone clicking this is telling us they are
 * the owner and the camera is wrong about them; without the reset their absence would still be
 * long expired and the panel would slam back the moment the cooldown lapsed, leaving them
 * dismissing it every few seconds. This buys them a full minute — the same grace a recognized
 * face gets — which is exactly what the button promises.
 */
export function dismissFaceIntruder() {
  clearIntruder();
  resetPending();
  ownerLastSeenAt = Date.now();
  cooldownUntil = Date.now() + ACTION_COOLDOWN_MS;
}
